import { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { needItSoonerApi } from 'app/modules/NeedItSooner/needItSoonerApi';
import { routes } from 'app/routes';
import { Button } from 'app/theme/Button';
import { Form } from 'app/theme/Form';
import { Grid, GridColumn } from 'app/theme/Grid';
import { CalendarIcon } from 'app/theme/Icons/CalendarIcon';
import { EmailIcon } from 'app/theme/Icons/EmailIcon';
import { NotesCommentsIcon } from 'app/theme/Icons/NotesCommentsIcon';
import { PhoneIcon } from 'app/theme/Icons/PhoneIcon';
import { LinearLoader } from 'app/theme/LinearLoader';
import { breakpoint } from 'app/theme/theme';
import { common } from 'app/translations/dictionary/common';
import { needItSooner } from 'app/translations/dictionary/needItSooner';
import { dateInFuture, email, phoneNumber, required } from 'app/translations/validators';
import { useHttpErrorAlert } from 'pkg/Alerts/useHttpErrorAlert';
import { ConnectedFileUploader } from 'pkg/components/ConnectedFileUploader';
import { DateTimePickerWithValidation } from 'pkg/components/DateTimePickerWithValidation';
import { InputWithIconAndValidation } from 'pkg/components/InputWithIconAndValidation';
import { TextAreaWithIconAndValidation } from 'pkg/components/TextAreaWithIconAndValidation';
import { HttpError } from 'pkg/httpClient/httpClient';

import { UserContext } from '../Context';
import { useCurrency } from '../CurrencyProvider';
import { PriceOptionType } from '../PriceOptions/PriceOption';
import { usePriceOptions } from '../PriceOptions/usePriceOptions';
import { useFileUploader } from '../TranslationQuote/TranslationQuotePricing/useFileUploader';
import { NeedItSooner } from './NeedItSooner';

const createInitialForm = (initialEmail: string, phone?: string, files?: string[]) => ({
  deadline: null,
  email: initialEmail,
  phone: phone || '',
  notes: '',
  files: files || [],
});

const FormContent = styled.div`
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
`;

const SubmitButton = styled(Button)`
  &&& {
    margin-top: 1rem;
    height: 4.8rem;
    width: 100%;
    font-size: 1.8rem;
    box-shadow: 0.1rem 0.1rem 0.7rem rgba(0, 0, 0, 0.16);
    ${breakpoint.up.md`
      height: 5.6rem;
      font-size: 2.4rem;
      margin-top: 0rem;
      box-shadow: 0.3rem 0.3rem 0.7rem rgba(186, 47, 68, 0.4);
    `};
  }
`;

const StyledPhoneIcon = styled(PhoneIcon)`
  margin-top: -0.2rem;
  margin-left: 0.2rem;
`;

export const NeedItSoonerForm = (): JSX.Element => {
  const { user, setUser } = useContext(UserContext);
  const initialEmail = user?.email || '';
  const history = useHistory();
  const { currency } = useCurrency();
  const setHttpErrorAlert = useHttpErrorAlert();
  const { id, option } = useParams<{ id: string; option: string }>();
  const { mutate: sendForm, isLoading } = useMutation<Omit<NeedItSooner, 'quoteId'>, HttpError, NeedItSooner>(
    needItSoonerApi.post,
    {
      onSuccess: (_, { email: emailValue }) => {
        setUser({ email: emailValue, name: '' });
        return history.replace(`${routes.TRANSLATION_QUOTE.THANK_YOU}/${id}`);
      },
      onError: setHttpErrorAlert,
    }
  );
  const { files } = useFileUploader();
  const { phone } = usePriceOptions();
  const methods = useForm<NeedItSooner>({
    defaultValues: createInitialForm(initialEmail, phone, files),
  });

  const handleSubmit = (formData: NeedItSooner) =>
    sendForm({ ...formData, quoteId: id, option: Number(option) as PriceOptionType, currency });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(handleSubmit)}>
        <FormContent>
          <Grid>
            <GridColumn width={16}>
              <DateTimePickerWithValidation
                name="deadline"
                placeholder={needItSooner.deadline}
                fluid
                disabled={isLoading}
                inputIcon={<CalendarIcon />}
                rules={dateInFuture}
              />
            </GridColumn>
            <GridColumn width={16}>
              <InputWithIconAndValidation
                fluid
                disabled={isLoading}
                name="email"
                placeholder={needItSooner.email}
                inputIcon={<EmailIcon />}
                rules={{ ...required, ...email }}
              />
            </GridColumn>
            <GridColumn width={16}>
              <InputWithIconAndValidation
                fluid
                disabled={isLoading}
                name="phone"
                placeholder={needItSooner.phone}
                inputIcon={<StyledPhoneIcon />}
                rules={phoneNumber}
              />
            </GridColumn>

            <GridColumn width={16}>
              <TextAreaWithIconAndValidation
                name="notes"
                placeholder={needItSooner.notes}
                rows={5}
                resize="none"
                textAreaIcon={<NotesCommentsIcon />}
              />
            </GridColumn>

            <GridColumn width={16}>
              <ConnectedFileUploader areaOnly />
            </GridColumn>
            <GridColumn width={16}>
              <SubmitButton disabled={isLoading} fluid type="submit" primary>
                {common.submit}
              </SubmitButton>
              <LinearLoader $isLoading={isLoading} />
            </GridColumn>
          </Grid>
        </FormContent>
      </Form>
    </FormProvider>
  );
};
